import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';

// Type-only import so the `{@link AlertDialog}` JSDoc reference below resolves.
// eslint-disable-next-line unused-imports/no-unused-imports, @typescript-eslint/no-unused-vars
import { type AlertDialog } from './AlertDialog';
import { Button } from './Button';
import { Icon } from './Icon';
import { useShadowRoot } from './ShadowRootContext';
import { cn } from './utils/cn';

/**
 * Modal panel for content the user works with: forms, comparisons, detail
 * views. Compose with {@link DialogTrigger}, {@link DialogContent}
 * (containing {@link DialogHeader}, {@link DialogTitle},
 * {@link DialogDescription}, and an optional {@link DialogFooter}).
 *
 * For yes/no confirmations and irreversible actions, use {@link AlertDialog}
 * instead.
 *
 * @example
 * <Dialog>
 *   <DialogTrigger asChild>
 *     <Button variant="outline">Edit Profile</Button>
 *   </DialogTrigger>
 *   <DialogContent size="sm">
 *     <DialogHeader>
 *       <DialogTitle>Edit Profile</DialogTitle>
 *       <DialogDescription>
 *         Make changes to your profile here. Click save when you're done.
 *       </DialogDescription>
 *     </DialogHeader>
 *     <ProfileForm />
 *     <DialogFooter>
 *       <DialogClose asChild>
 *         <Button variant="outline">Cancel</Button>
 *       </DialogClose>
 *       <Button>Save</Button>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 */
function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

/** Toggle that opens the parent {@link Dialog}. Wrap a `Button` (or any clickable) with `asChild`. */
function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

/** Renders dialog content into a portal. Used internally by {@link DialogContent}. */
function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

/** Closes the parent {@link Dialog}. Wrap a `Button` with `asChild` to render your own dismissal control. */
function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/** Backdrop behind {@link DialogContent}. Used internally; consumers don't render it directly. */
function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Modal panel that holds the dialog's header, body, and footer. The `size`
 * prop selects a width preset; `showCloseButton` toggles the inline close
 * affordance in the top-right corner.
 *
 * By default `DialogContent` renders into a portal (and it automatically
 * picks the appropriate target when rendered in a Shadow DOM). Pass
 * `usesPortal={false}` to skip portaling and render the dialog inline at the
 * call site; useful when the call site is already the right rendering
 * context and re-portaling would lift the dialog out of it.
 */
function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'default',
  usesPortal,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  size?: 'sm' | 'default' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  usesPortal?: boolean;
}) {
  const { container, fullPageContainer } = useShadowRoot();
  const portalTarget = fullPageContainer ?? container;

  const content = (
    <>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-size={size}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-1/2 gap-4 rounded-xl bg-popover p-4 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-[size=2xl]:sm:max-w-2xl data-[size=3xl]:sm:max-w-3xl data-[size=4xl]:sm:max-w-4xl data-[size=default]:sm:max-w-md data-[size=full]:sm:max-w-[90vw] data-[size=lg]:sm:max-w-lg data-[size=sm]:sm:max-w-sm data-[size=xl]:sm:max-w-xl data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close data-slot="dialog-close" asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Close"
              className="absolute top-2 right-2"
            >
              <Icon name="X" size="auto" />
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </>
  );

  if (usesPortal === false) {
    return content;
  }

  return <DialogPortal container={portalTarget}>{content}</DialogPortal>;
}

/** Header section. Holds {@link DialogTitle} and {@link DialogDescription}. */
function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

/**
 * Footer section. Holds the action buttons. Pass `showCloseButton` to
 * append a default outline-style "Close" button alongside your actions.
 */
function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 p-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

/** Headline of the dialog. Required for accessibility. */
function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-md leading-none font-medium', className)}
      {...props}
    />
  );
}

/** Optional short explanation rendered under {@link DialogTitle}. */
function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
